<!-- SearchMemberAccountInput.vue -->
<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import debounce from "lodash.debounce";

const props = defineProps({
  resetKey: {
    type: [Number, String],
    default: 0,
  },
});

const store = useManagedAccountStore();

// Nunca inicializa a partir da store para evitar reaproveitamento de busca antiga.
const search = ref("");
const inputRef = ref(null);

// Proteção extra contra autofill/reaproveitamento do browser logo após remount.
const isTemporarilyReadonly = ref(true);

function syncStoreSearchTerm(value) {
  if (typeof store.setSearchTerm === "function") {
    store.setSearchTerm(value);
    return;
  }

  store.relationshipSearchTerm = value;
}

function clearSearch() {
  search.value = "";

  // Garante limpeza também do valor real do input caso o browser tenha preenchido
  // o campo sem passar pelo v-model.
  if (inputRef.value) {
    inputRef.value.value = "";
  }

  syncStoreSearchTerm("");
}

const debouncedSearch = debounce(() => {
  syncStoreSearchTerm(search.value);
}, 400);

watch(search, () => {
  debouncedSearch();
});

watch(
  () => props.resetKey,
  () => {
    debouncedSearch.cancel();
    clearSearch();
  }
);

onMounted(async () => {
  await nextTick();

  // Primeiro limpa imediatamente.
  clearSearch();

  // Depois limpa de novo no próximo ciclo, pois alguns browsers/autofill
  // preenchem inputs após o mounted/nextTick.
  window.setTimeout(() => {
    clearSearch();
    isTemporarilyReadonly.value = false;
  }, 80);
});

onBeforeUnmount(() => {
  debouncedSearch.cancel();
});
</script>

<template>
  <div class="search-input-wrapper">
    <div class="icon-wrapper">
      <svg
        aria-hidden="true"
        class="search-icon"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
          clip-rule="evenodd"
        ></path>
      </svg>
    </div>

    <input
      ref="inputRef"
      v-model="search"
      type="search"
      id="member-account-search"
      name="member-account-search-ignore-autofill"
      placeholder="Buscar por nome, username ou e-mail"
      class="input-base"
      autocomplete="new-password"
      autocapitalize="off"
      autocorrect="off"
      spellcheck="false"
      :readonly="isTemporarilyReadonly"
      style="padding-left: 2rem"
      @focus="isTemporarilyReadonly = false"
    />
  </div>
</template>

<style scoped>
.search-icon {
  width: 1rem;
}
</style>
